import { FC, MouseEvent, useContext, useState, } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableSortLabel,
  TableBody,
  Link,
  Skeleton,
  Divider,
  Container,
  Button, } from '@material-ui/core';
import { Scrollbar } from 'src/components/scrollbar';
import Proptypes from 'prop-types';
import { ResourceUnavailable } from 'src/components/resource-unavailable';
import { ResourceError } from 'src/components/resource-error';
import { CustomerAccountDeletionRequestsDetails, UpdateStatusUserDeleteRequest } from 'src/interfaces/customers/customers';
import { getCustomerAccountDeleteStatusColor } from 'src/components/masters/customFunctions';
import { format } from 'date-fns';
import { UserDeleteRequestStatus } from 'src/enums/common';
import { StatusReasonDialog } from 'src/components/common/status-reason-dialog';
import loadingContext from 'src/loderContext';
import { ResponseCodes } from 'src/enums/responseCodes';
import toast from 'react-hot-toast';
import { accountDeletionRequestApi } from 'src/api/customerADApi';
import { Pagination } from '../../pagination';

interface DeleteCustomerTableProps {
  requests: CustomerAccountDeletionRequestsDetails[];
  requestsCount: number;
  error: string;
  isLoading: boolean;
  onPageChange: (newPage: number) => void;
  onSortChange: (event: MouseEvent<HTMLElement>, property: string) => void;
  page: number;
  sort: 'asc' | 'desc';
  sortBy: string;
  onCrud: () => void;
}

const columns = [
  {
    id: 'createdAt',
    label: 'Request Date',
    sortDisable: true
  },
  {
    id: 'id',
    label: 'Request Id',
    sortDisable: true
  },
  {
    id: 'customerName',
    label: 'Customer Name',
    sortDisable: true
  },
  {
    id: 'status',
    label: 'Status',
    sortDisable: true
  },
  {
    id: 'remark',
    label: 'Remark',
    sortDisable: true
  },
  {
    id: 'action',
    label: 'Action',
    sortDisable: true
  }
];

export const CustomerRequestTable: FC<DeleteCustomerTableProps> = (props) => {
  const {
    requests,
    requestsCount,
    error,
    isLoading,
    onPageChange,
    onSortChange,
    page,
    sort,
    sortBy,
    onCrud
  } = props;
  const displayError = Boolean(!isLoading && error);
  const displayUnavailable = Boolean(!isLoading && !error && !requests?.length);

  const [openStatusChangeDialog, setOpenStatusChangeDialog] = useState(false);
  const [currentStatus, setCurrentStatus] = useState('');
  const [requestData, setRequestData] = useState<CustomerAccountDeletionRequestsDetails>();
  const { setLoading } = useContext(loadingContext);

  const setRemark = (data: string) => {
    setLoading(true);
    const updateStatusData : UpdateStatusUserDeleteRequest = {
      username: requestData.userName,
      status: currentStatus,
      remark: data,
    };
    accountDeletionRequestApi.updateStatus(requestData.id, updateStatusData).then((res) => {
      if (res.statusCode === ResponseCodes.OK) {
        toast.success('Status of Request Modified Successfully');
        onCrud();
      } else {
        toast.error('Something went wrong');
      }
    });

    setLoading(false);
  };

  const renderStatus = (status) => {
    const color = getCustomerAccountDeleteStatusColor(status);
    return (
      <Container
        disableGutters
        sx={{
          m: 0,
          px: 0,
          color: `${color}`,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-start',
          alignItems: 'center'
        }}
      >
        <Box
          sx={{
            backgroundColor: `${color}`,
            borderRadius: '50%',
            height: 12,
            width: 12,
            ml: 0,
            my: 1,
            mr: 1
          }}
        />
        {status}
      </Container>
    );
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column'
        }}
      >
        <Scrollbar>
          <Table
            size="small"
            sx={{
              minWidth: 1000,
              maxWidth: '100%'
            }}
          >
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.id === 'action' ? 'center' : 'left'}
                  >
                    <TableSortLabel
                      active={sortBy === column.id}
                      direction={sortBy === column.id ? sort : 'asc'}
                      disabled={isLoading}
                      hideSortIcon={column.sortDisable}
                      onClick={!column.sortDisable
                        ? (event) => onSortChange(event, column.id) : null}
                    >
                      {column.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {requests?.map((customer) => (
                <TableRow
                  hover
                  key={customer.userName}
                >
                  <TableCell>
                    {format(new Date(customer.createdAt), 'MMM dd, yyyy')}
                  </TableCell>
                  <TableCell>
                    {customer.id}
                  </TableCell>
                  <TableCell
                    align="left"
                  >
                    <Link
                      color="primary"
                      component={RouterLink}
                      sx={{ display: 'block' }}
                      to={`/dashboard/customers/view/${customer.userName}`}
                      underline="none"
                      variant="subtitle2"
                    >
                      {customer.customerName}
                    </Link>
                  </TableCell>
                  <TableCell
                    align="justify"
                  >
                    {renderStatus(customer.status)}
                  </TableCell>
                  <TableCell
                    align="justify"
                  >
                    {customer.remark || '--'}
                  </TableCell>
                  <TableCell
                    align="center"
                  >
                    <>
                      <Button
                        color="primary"
                        sx={{
                          mr: 1
                        }}
                        disabled={customer.status !== UserDeleteRequestStatus.OPEN}
                        onClick={() => {
                          setRequestData(customer);
                          setCurrentStatus(UserDeleteRequestStatus.REVOKED);
                          setOpenStatusChangeDialog(true);
                        }}
                        variant="contained"
                      >
                        Revoke
                      </Button>
                      <Button
                        color="error"
                        disabled={customer.status !== UserDeleteRequestStatus.OPEN}
                        onClick={() => {
                          setRequestData(customer);
                          setCurrentStatus(UserDeleteRequestStatus.DELETED);
                          setOpenStatusChangeDialog(true);
                        }}
                        variant="contained"
                      >
                        Delete
                      </Button>
                    </>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Scrollbar>
        {isLoading && (
          <Box sx={{ p: 2 }}>
            <Skeleton height={42} />
            <Skeleton height={42} />
            <Skeleton height={42} />
          </Box>
        )}
        {displayError && (
          <ResourceError
            error={error}
            sx={{
              flexGrow: 1,
              m: 2
            }}
          />
        )}
        {displayUnavailable && (
          <ResourceUnavailable
            sx={{
              flexGrow: 1,
              m: 2
            }}
          />
        )}
        <Divider sx={{ mt: 'auto' }} />
        <Pagination
          disabled={isLoading}
          onPageChange={onPageChange}
          page={page}
          rowsCount={requestsCount}
        />
      </Box>
      <StatusReasonDialog
        onClose={() => setOpenStatusChangeDialog(false)}
        onSubmit={setRemark}
        open={openStatusChangeDialog}
        title="Are You Sure"
        subtitle={`Do you really want to ${currentStatus === UserDeleteRequestStatus.REVOKED ? UserDeleteRequestStatus.REVOKE : UserDeleteRequestStatus.DELETE} Profile ?`}
        placeHolderValue="Remark ..."
        submitbuttonName={currentStatus === UserDeleteRequestStatus.REVOKED
          ? UserDeleteRequestStatus.REVOKE : UserDeleteRequestStatus.DELETE}
      />
    </>
  );
};

CustomerRequestTable.propTypes = {
  requests: Proptypes.array,
  requestsCount: Proptypes.number,
  error: Proptypes.string,
  isLoading: Proptypes.bool,
  onPageChange: Proptypes.func,
  onSortChange: Proptypes.func,
  page: Proptypes.number,
  sort: Proptypes.oneOf(['asc', 'desc']),
  sortBy: Proptypes.string,
  onCrud: Proptypes.func
};
